/*
 * huit-header-hero.js
 * ------------------------------------------------------------------
 * Drives the two header states on the home page, keyed to the HERO's
 * bottom boundary rather than generic scroll / sticky-state.
 *
 *   data-huit-header="over-hero"  -> header sits over the pinned hero:
 *                                    transparent background, white content.
 *   data-huit-header="past-hero"  -> hero has scrolled past: solid white
 *                                    bar with ink content (the resting look).
 *
 * Why a dedicated trigger: the hero (`[data-huit-hero]`) is a pinned,
 * scroll-scrubbed section ~560vh tall. Horizon's native header.js flips
 * `data-sticky-state` to "active" within the first few px of scroll, which
 * would solidify the header almost immediately. That is wrong here: the
 * header must stay transparent/white for the ENTIRE hero and only go solid
 * once the hero's bottom has passed above the header. So we observe the hero
 * element with a bottom rootMargin equal to the header height and toggle our
 * own attribute, independent of header.js's data-sticky-state.
 *
 * Behaviour:
 *  - No hero on the page (non-home / hero removed) -> do nothing. The header
 *    keeps whatever state the markup shipped (its normal solid resting look).
 *  - Theme-editor safe: re-queries + re-observes on shopify:section:load,
 *    tears down on shopify:section:unload. Listeners bound once.
 *  - Responsive: header height differs across breakpoints (desktop logo clamp
 *    vs mobile 52px + announcement bar). A debounced resize rebuilds the
 *    IntersectionObserver with a fresh header-height margin so the flip point
 *    doesn't drift after a resize/rotate.
 *  - Fail-safe: if this script never runs (JS off / error), the header keeps
 *    its SSR attribute (over-hero on home) which is the correct top-of-page
 *    state, and the CSS still renders a readable header.
 *
 * Hero pin recompute: enabling the native transparent header makes
 * #header-component position:absolute (out of flow), which shifts the hero up.
 * The hero's GSAP ScrollTrigger pin (owned by the hero JS, NOT touched here) is
 * measured before that layout settles, so the pin can release early and leave a
 * white gap. After the header has applied its state we trigger a guarded,
 * coalesced public ScrollTrigger.refresh() so the hero pin recomputes against
 * the transparent-header layout. We only call the public .refresh() API; we
 * never import GSAP, read its config, or touch the hero.
 *
 * Vanilla JS, no dependencies. Resilient to GSAP / hero JS being absent.
 */
(function () {
  'use strict';

  var HEADER_SELECTOR = '#header-component';
  var HERO_SELECTOR = '[data-huit-hero]';
  var STATE_ATTR = 'data-huit-header';
  var OVER = 'over-hero';
  var PAST = 'past-hero';

  var observer = null;
  var observedHero = null;

  // Cached header height. Recomputed only on (re)observe and resize, never per
  // scroll tick, so the fallback loop avoids a forced reflow on the header
  // every frame.
  var cachedHeaderHeight = 60;

  // Scroll-fallback state: refs kept so disconnect() can remove the listeners.
  var scrollBound = false;
  var onScrollHandler = null;

  // Coalesced ScrollTrigger refresh state (see requestRefresh).
  var refreshScheduled = false;

  function getHeader() {
    return document.querySelector(HEADER_SELECTOR);
  }

  function getHero() {
    return document.querySelector(HERO_SELECTOR);
  }

  function measureHeaderHeight() {
    var header = getHeader();
    if (header) {
      var h = header.getBoundingClientRect().height;
      if (h > 0) {
        cachedHeaderHeight = h;
        return cachedHeaderHeight;
      }
    }
    // Fall back to the theme's --header-height custom property, else a sane default.
    var raw = getComputedStyle(document.body).getPropertyValue('--header-height');
    var parsed = parseFloat(raw);
    cachedHeaderHeight = isNaN(parsed) ? 60 : parsed;
    return cachedHeaderHeight;
  }

  function setState(state) {
    var header = getHeader();
    if (!header) return;
    if (header.getAttribute(STATE_ATTR) !== state) {
      header.setAttribute(STATE_ATTR, state);
    }
  }

  // Schedule a single, coalesced public ScrollTrigger.refresh() so the hero's
  // pin recomputes against the transparent-header (position:absolute) layout.
  // Guarded: no-op unless the hero is present AND ScrollTrigger exposes a
  // refresh() function. We never import GSAP or touch the hero's config; this
  // only calls the public API the hero script already loaded. Runs on the next
  // tick so GSAP (loaded by the hero script) has a chance to register.
  function requestRefresh() {
    if (!getHero()) return;
    if (refreshScheduled) return;
    refreshScheduled = true;
    window.requestAnimationFrame(function () {
      refreshScheduled = false;
      var ST = window.ScrollTrigger;
      if (ST && typeof ST.refresh === 'function') {
        ST.refresh();
      }
    });
  }

  function disconnect() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    // Tear down the scroll-fallback listeners and reset its guard so a later
    // re-observe() can cleanly re-bind.
    if (scrollBound && onScrollHandler) {
      window.removeEventListener('scroll', onScrollHandler);
      window.removeEventListener('resize', onScrollHandler);
    }
    scrollBound = false;
    onScrollHandler = null;
    observedHero = null;
  }

  function observe() {
    disconnect();

    var hero = getHero();
    var header = getHeader();
    if (!hero || !header) {
      // Nothing to track. Leave the header in whatever (solid) state it shipped.
      return;
    }

    observedHero = hero;

    // Measure the header once here; reused by the seed and the IO margin.
    var h = Math.round(measureHeaderHeight());

    // Seed the state immediately so there's no flash before the first callback.
    syncFromRect(hero);

    if (!('IntersectionObserver' in window)) {
      // No IO support: degrade to a light scroll listener doing the same maths.
      bindScrollFallback();
      return;
    }

    // rootMargin top = -headerHeight: the observed root is shrunk from the top
    // by the header's height, so the hero is considered "intersecting" only
    // while any part of it is still below the header band. Once the hero's
    // bottom rises above the header, intersection goes false -> past-hero.
    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          setState(entries[i].isIntersecting ? OVER : PAST);
        }
      },
      {
        root: null,
        rootMargin: '-' + h + 'px 0px 0px 0px',
        threshold: 0
      }
    );
    observer.observe(hero);

    // The header has now applied/seeded its state and is laid out. Ask the hero
    // pin to recompute against this layout (transparent header = out of flow),
    // which clears the white-gap measurement race. Covers both init and the
    // debounced resize re-observe (both go through observe()).
    requestRefresh();
  }

  // Compute state directly from geometry (used for the initial seed and the
  // scroll fallback). over-hero while the hero bottom is still below the
  // header's bottom edge; past-hero once it has scrolled above it. Uses the
  // cached header height (no per-tick reflow on the header).
  function syncFromRect(hero) {
    if (!hero) hero = observedHero;
    if (!hero) return;
    var rect = hero.getBoundingClientRect();
    setState(rect.bottom > cachedHeaderHeight ? OVER : PAST);
  }

  function bindScrollFallback() {
    if (scrollBound) return;
    scrollBound = true;
    var ticking = false;
    onScrollHandler = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        // Guard against a stale/removed hero (e.g. section unloaded between ticks).
        if (!observedHero || !observedHero.isConnected) {
          ticking = false;
          return;
        }
        syncFromRect(observedHero);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScrollHandler, { passive: true });
    window.addEventListener('resize', onScrollHandler, { passive: true });
  }

  function init() {
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // The white-gap is a load-time measurement race: the hero pin is measured
  // before the 3D webp logo, fonts and the hero video poster settle (which is
  // also when GSAP/ScrollTrigger, loaded by the hero script, is reliably ready).
  // A second guarded refresh on window.load recomputes the pin against the
  // final layout. Coalesced + no-op when no hero / no ScrollTrigger.
  window.addEventListener('load', requestRefresh, { once: true });

  // Resize/rotate: the header height changes across breakpoints, so the IO's
  // baked-in rootMargin drifts. Debounce and re-run observe() (which disconnects
  // the old observer first and rebuilds it with a fresh header-height margin).
  // Bound once at module scope so it never double-binds. No-op when no hero.
  var resizeTimer = null;
  window.addEventListener(
    'resize',
    function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (getHero()) observe();
      }, 150);
    },
    { passive: true }
  );

  // Theme editor: a section being added/removed/reordered re-renders markup.
  // Re-query and re-observe so we always track the live hero/header nodes.
  document.addEventListener('shopify:section:load', function () {
    observe();
  });
  document.addEventListener('shopify:section:unload', function () {
    // If the hero was the section removed, stop tracking and fall back to solid.
    if (!getHero()) {
      disconnect();
      setState(PAST);
    } else {
      observe();
    }
  });
})();
